import { Http } from 'lib'

import { InitiativeSubmissionState, initiativeSubmissionInitialState, ValidationErrorResult } from './store/State'

import {
  setIsLoadingInitiativeData,
  setIsSavingInitiativeData,
  setGpaGrade,
  setGpaValue,
  setQuestionAnswer,
  setQuestionChoice,
  setCurrentDateTime,
  setLastAutoSaveDate,
  setValidationError,
  setIsSubmitting,
  setIsSuccessDialogOpen,
  setIsIncompleteProfileDialogOpen,
  completeInitiativeDataLoading,
  setInitiativeData,
  setValidationState,
  reset,
  InitiativeSubmissionAction
} from './store/Actions'

import IInitiativeSubmissionData from 'types/IInitiativeSubmissionData'
import IInitiativeSubmissionPayload from 'types/IInitiativeSubmissionPayload'
import { IInitiativeAnswerData, IInitiativeAnswersSubmission } from './Interfaces'
import IUserData from 'types/sign-in/IUserData'
import IApiResponse from 'types/IApiResponse'
import ServiceProviderBase from 'lib/ServiceProviderBase'
import reducer from './store/Reducer'
import apiUrls from 'config/constants/api-urls-addresses'
import { QuestionType } from 'types/IInitiativeSubmissionQuestions'

const URL_CHECKER_REGEX = /^((https|http):\/\/)(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/

class InitiativeSubmissionService extends ServiceProviderBase<InitiativeSubmissionState, InitiativeSubmissionAction> {
  constructor() {
    super(initiativeSubmissionInitialState)
  }

  reduce(state: InitiativeSubmissionState, action: InitiativeSubmissionAction): InitiativeSubmissionState {
    return reducer(state, action)
  }

  parseDataToAnswerState(submissionData: IInitiativeSubmissionData): IInitiativeAnswerData {
    let gpaGrade: 'GRADUATE' | 'UNDERGRADUATE' | 'DOES_NOT_APPLY' | null = null

    if (submissionData.doesNotApplyGpa) {
      gpaGrade = 'DOES_NOT_APPLY'
    } else if (submissionData.graduate) {
      gpaGrade = 'GRADUATE'
    } else if (submissionData.undergraduate) {
      gpaGrade = 'UNDERGRADUATE'
    }

    const gpa: number =
      submissionData.gpa || (submissionData.graduateGpa ? submissionData.graduateGpa : submissionData.undergraduateGpa)

    const questions = submissionData.questions.reduce((acc, item) => {
      return Object.assign({}, acc, { [item.id.toString()]: { answer: item.answer, choices: item.choices } })
    }, {})

    return {
      gpaGrade,
      gpa,
      questions
    }
  }

  parseAnswerStateToPayload(answerState: IInitiativeAnswerData): IInitiativeSubmissionPayload {
    return {
      gpa: answerState.gpa,
      graduate: answerState.gpaGrade === 'GRADUATE',
      undergraduate: answerState.gpaGrade === 'UNDERGRADUATE',
      doesNotApplyGpa: answerState.gpaGrade === 'DOES_NOT_APPLY',
      questions: Object.entries(answerState.questions).map(([questionId, answer]) => ({
        questionId: parseInt(questionId, 10),
        answer: (answer || {}).answer,
        choices: (answer || {}).choices
      }))
    }
  }

  getGpaSchoolType(gpaGrade: 'GRADUATE' | 'UNDERGRADUATE' | 'DOES_NOT_APPLY' | null): string | null {
    switch (gpaGrade) {
      case 'GRADUATE':
        return 'graduate'
      case 'UNDERGRADUATE':
        return 'undergraduate'
      default:
        return null
    }
  }

  parseStateToSubmissionPayload(state: InitiativeSubmissionState): IInitiativeAnswersSubmission {
    return {
      enterpriseId: state.initiativeData.enterpriseId,
      initiativeId: state.initiativeData.id,
      responses: {
        gpaValue: state.answers.gpa,
        gpaSchoolType: this.getGpaSchoolType(state.answers.gpaGrade),
        questionAnswers: Object.entries(state.answers.questions).map(([id, answer]) => ({
          questionId: parseInt(id, 10),
          answer: (answer || {}).answer,
          choices: (answer || {}).choices
        }))
      }
    }
  }

  setInitiativeData(initiativeData: IInitiativeSubmissionData) {
    this.dispatch(setInitiativeData(initiativeData))
  }

  async loadInitiativeInformation(enterpriseId: string, submissionLinkCode: string): Promise<string> {
    if (this.state.isLoadingInitiative) {
      return null
    }

    this.dispatch(setIsLoadingInitiativeData(true))

    const [data, , error] = await Http.get<IInitiativeSubmissionData>(
      apiUrls.enterprise.loadInitiativeSymbol(enterpriseId, submissionLinkCode)
    )

    if (error) {
      return error
    }

    this.dispatch(completeInitiativeDataLoading(data, this.parseDataToAnswerState(data)))

    return null
  }

  async saveInitiativeSubmission(): Promise<string | null> {
    const payload = this.parseAnswerStateToPayload(this.state.answers)

    this.dispatch(setIsSavingInitiativeData(true))

    const [, , error] = await Http.post<IInitiativeSubmissionPayload, IInitiativeSubmissionData>(
      apiUrls.enterprise.saveInitiativeSubmissionDraft(
        this.state.initiativeData.enterpriseId,
        this.state.initiativeData.id
      ),
      payload
    )

    this.dispatch(setIsSavingInitiativeData(false))
    this.dispatch(setLastAutoSaveDate(new Date().toISOString()))

    return error
  }

  validateChoiceAnswer(value: number[], questionType: QuestionType): ValidationErrorResult {
    if (questionType === QuestionType.MULTIPLE_CHOICE && value.length === 0) {
      return ValidationErrorResult.TOO_FEW_CHOICES
    }

    return ValidationErrorResult.VALID_ANSWER
  }

  validateTextAnswer(value: string, questionType: QuestionType): ValidationErrorResult {
    if (!Boolean(value.trim())) {
      return ValidationErrorResult.EMPTY_ANSWER
    }

    if (questionType === QuestionType.MEDIA_LINK && !URL_CHECKER_REGEX.test(value)) {
      return ValidationErrorResult.INVALID_LINK
    }

    return ValidationErrorResult.VALID_ANSWER
  }

  validateAnswer(
    questionId: number,
    value: string | number[],
    validationMap: { [key: string]: QuestionType }
  ): ValidationErrorResult {
    if ([QuestionType.CHECKBOX, QuestionType.MULTIPLE_CHOICE].includes(validationMap[questionId])) {
      return this.validateChoiceAnswer(value as number[], validationMap[questionId])
    }

    return this.validateTextAnswer(value as string, validationMap[questionId])
  }

  async submitInitiativeAnswers(): Promise<[IUserData | null, string | null]> {
    if (['UNDERGRADUATE', 'GRADUATE'].includes(this.state.answers.gpaGrade) && !this.state.answers.gpa) {
      this.dispatch(setValidationError(true))
      return [null, 'Validation error']
    }

    const payload = this.parseStateToSubmissionPayload(this.state)

    const validationMap = this.state.initiativeData.questions.reduce((acc, item) => {
      return {
        ...acc,
        [item.id]: item.questionType
      }
    }, {})

    const validationResult = payload.responses.questionAnswers.reduce<{ [key: string]: ValidationErrorResult }>(
      (acc, answer) => ({
        ...acc,
        [answer.questionId]: this.validateAnswer(answer.questionId, answer.answer || answer.choices, validationMap)
      }),
      {}
    )

    this.dispatch(setValidationState(validationResult))

    if (Object.entries(validationResult).some(([, result]) => result !== 'VALID_ANSWER')) {
      this.dispatch(setValidationError(true))
      return [null, 'Validation error']
    }

    this.dispatch(setValidationError(false))
    this.dispatch(setIsSubmitting(true))

    const [user, , error] = await Http.post<IInitiativeAnswersSubmission, IApiResponse<IUserData>>(
      apiUrls.enterprise.submission(),
      payload
    )

    this.dispatch(setIsSubmitting(false))

    if (error === 'Pointr profile is incomplete') {
      this.dispatch(setIsIncompleteProfileDialogOpen(true))
      return [null, null]
    } else if (error) {
      return [null, error]
    }

    this.dispatch(setIsSuccessDialogOpen(true))

    return [user.data, null]
  }

  setGpaGrade(gpaGrade: 'GRADUATE' | 'UNDERGRADUATE' | 'DOES_NOT_APPLY' | null) {
    if (this.state.answers.gpaGrade !== gpaGrade) {
      this.dispatch(setGpaGrade(gpaGrade))
      if (gpaGrade === 'DOES_NOT_APPLY') {
        this.dispatch(setGpaValue(null))
      }
    }
  }

  setGpaValue(gpaValue: string) {
    const value = parseFloat(gpaValue)
    if (!!value && value > 0 && value <= 4 && this.state.answers.gpa !== value) {
      this.dispatch(setGpaValue(value))
    }
  }

  async setQuestionAnswer(questionId: number, answer: string) {
    const currentAnswer = this.state.answers.questions[questionId]
    if (currentAnswer !== answer) {
      this.dispatch(setQuestionAnswer(questionId, answer))
      await this.saveInitiativeSubmission()
    }
  }

  async setQuestionChoice(questionId: number, choices: number[]) {
    this.dispatch(setQuestionChoice(questionId, choices))
    await this.saveInitiativeSubmission()
  }

  setCurrentDateTime() {
    this.dispatch(setCurrentDateTime(new Date().toISOString()))
  }

  toggleIsSuccessDialogOpen() {
    this.dispatch(setIsSuccessDialogOpen(!this.state.isSuccessDialogOpen))
  }

  toggleIsIncompleteProfileDialogOpen() {
    this.dispatch(setIsSuccessDialogOpen(!this.state.isIncompleteProfileDialogOpen))
  }

  toggleValidationError() {
    this.dispatch(setValidationError(!this.state.validationError))
  }

  reset() {
    this.dispatch(reset())
  }
}

let instance: InitiativeSubmissionService = null

export default (() => {
  if (!instance) {
    instance = new InitiativeSubmissionService()
  }
  return instance
})()
